import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MeWorkspace } from '@/api/services/me';
import { useWorkspace } from '@/context/WorkspaceContext';
import { workspaceIcon } from '@/utils/workspaceIcons';
import { filterVisibleWorkspaces } from '@/utils/workspaceList';

function workspaceKey(item: MeWorkspace): string {
  return item.id ?? 'personal';
}

function isOrganizationOnlyPath(pathname: string): boolean {
  return (
    pathname === '/workspace' ||
    pathname.startsWith('/workspace/members') ||
    pathname.startsWith('/workspace/settings')
  );
}

type WorkspaceSwitcherProps = {
  /** Single section label above the control (avoids duplicate WORKSPACE labels). */
  sectionLabel?: string;
  compact?: boolean;
};

export default function WorkspaceSwitcher({ sectionLabel, compact = false }: WorkspaceSwitcherProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    canSwitchWorkspace,
    canManageOrganizations,
    activeOrganizationId,
    activeWorkspaceName,
    isPersonalMode,
    selectWorkspace,
    workspaces,
    workspacesLoading,
    workspaceVersion,
    refreshWorkspaces,
  } = useWorkspace();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const [position, setPosition] = useState({ top: 0, left: 0, width: 280 });

  useEffect(() => {
    if (open) {
      void refreshWorkspaces();
    }
  }, [open, refreshWorkspaces, workspaceVersion]);

  const items = useMemo(() => {
    const all = filterVisibleWorkspaces(workspaces?.items ?? []);
    const term = query.trim().toLowerCase();
    if (!term) return all;
    return all.filter((item) => item.name.toLowerCase().includes(term));
  }, [workspaces?.items, query]);

  const activeId = isPersonalMode ? 'personal' : activeOrganizationId;

  const updatePosition = () => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const width = Math.max(rect.width, 280);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    setPosition({ top: rect.bottom + 6, left, width });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', onScroll, true);
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query, open, items.length, workspaceVersion]);

  const displayName =
    activeWorkspaceName ??
    workspaces?.activeWorkspaceName ??
    (isPersonalMode ? 'Personal' : 'Organization');

  const handleSelect = async (item: MeWorkspace) => {
    const organizationId = item.id;
    setSwitching(true);
    setError(null);
    try {
      await selectWorkspace(organizationId);
      setOpen(false);
      setQuery('');
      if (!organizationId && isOrganizationOnlyPath(location.pathname)) {
        navigate('/', { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to switch workspace');
    } finally {
      setSwitching(false);
    }
  };

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    if (!items.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((i) => (i + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => (i - 1 + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[highlightIndex];
      if (item) void handleSelect(item);
    }
  };

  if (!canSwitchWorkspace) {
    if (!displayName) return null;
    return (
      <div className={`sidebar-workspace sidebar-workspace--static${compact ? ' sidebar-workspace--compact' : ''}`}>
        {sectionLabel && !compact ? (
          <span className="sidebar-workspace-label">{sectionLabel}</span>
        ) : null}
        <span className="sidebar-workspace-trigger sidebar-workspace-trigger--static">
          <span className="workspace-switcher__icon" aria-hidden>
            {isPersonalMode ? '🏠' : '🏢'}
          </span>
          <span className="workspace-switcher__label">{displayName}</span>
        </span>
      </div>
    );
  }

  return (
    <div className={`sidebar-workspace${compact ? ' sidebar-workspace--compact' : ''}`}>
      {sectionLabel && !compact ? (
        <span className="sidebar-workspace-label">{sectionLabel}</span>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        className="sidebar-workspace-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={menuId}
        disabled={switching}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="workspace-switcher__icon" aria-hidden>
          {isPersonalMode ? '🏠' : workspaceIcon({ workspaceType: 'organization' })}
        </span>
        <span className="workspace-switcher__label">{displayName}</span>
        <span className="workspace-switcher__chevron" aria-hidden>
          ▾
        </span>
      </button>
      {switching ? <span className="muted sidebar-workspace-status">Switching…</span> : null}
      {error ? <span className="sidebar-workspace-error">{error}</span> : null}

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={menuId}
            className="workspace-switcher__popover"
            role="listbox"
            aria-label="Switch workspace"
            style={{ top: position.top, left: position.left, width: position.width }}
            onKeyDown={onMenuKeyDown}
          >
            <div className="workspace-switcher__search-wrap">
              <input
                ref={searchRef}
                type="search"
                className="workspace-switcher__search"
                placeholder="Search workspaces…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Filter workspaces"
              />
            </div>

            <div className="workspace-switcher__list">
              {workspacesLoading && !items.length ? (
                <div className="workspace-switcher__empty muted">Loading workspaces…</div>
              ) : null}
              {!workspacesLoading && !items.length ? (
                <div className="workspace-switcher__empty muted">
                  {query.trim() ? 'No workspaces match your search.' : 'No workspaces available.'}
                </div>
              ) : null}
              {items.map((item, index) => {
                const key = workspaceKey(item);
                const isActive = (item.id ?? null) === (activeId === 'personal' ? null : activeId);
                return (
                  <button
                    key={key}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`workspace-switcher__item${index === highlightIndex ? ' is-highlighted' : ''}${
                      isActive ? ' is-active' : ''
                    }`}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => void handleSelect(item)}
                  >
                    <span className="workspace-switcher__icon" aria-hidden>
                      {workspaceIcon(item)}
                    </span>
                    <span className="workspace-switcher__item-label">{item.name}</span>
                    {isActive ? (
                      <span className="workspace-switcher__check" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {canManageOrganizations ? (
              <div className="workspace-switcher__footer">
                <button
                  type="button"
                  className="workspace-switcher__action"
                  onClick={() => {
                    setOpen(false);
                    navigate('/organizations/manage?create=1');
                  }}
                >
                  <span aria-hidden>➕</span> New organization
                </button>
                <Link
                  to="/organizations/manage"
                  className="workspace-switcher__action"
                  onClick={() => setOpen(false)}
                >
                  <span aria-hidden>⚙</span> Manage organizations
                </Link>
              </div>
            ) : null}
          </div>,
          document.body,
        )}
    </div>
  );
}
